import * as v from 'valibot';

/**
 * What somebody has asked of a run that is already going.
 *
 * An intent is a *request*, and the run's status is what actually happened —
 * two facts, kept apart on purpose. Cancelling reef is cooperative: a step in
 * the middle of a tool call finishes that call and stops at its next durable
 * checkpoint, because killing it mid-write is how you get a half-created pull
 * request nobody knows about. The gap between "cancel requested" and
 * "cancelled" is that wait, and a UI that collapsed the two would claim a run
 * had stopped while it was still writing.
 */
export const RunIntentKind = v.picklist(['cancel', 'pause', 'resume']);
export type RunIntentKind = v.InferOutput<typeof RunIntentKind>;

export const RunIntent = v.object({
  runId: v.string(),
  intent: RunIntentKind,
  reason: v.nullable(v.string()),
  requestedBy: v.nullable(v.string()),
  requestedAt: v.string(),
  /** Set once the runtime acted on it. Null means "asked for, not yet done". */
  observedAt: v.nullable(v.string()),
});
export type RunIntent = v.InferOutput<typeof RunIntent>;

export const RunControlRequest = v.object({
  intent: RunIntentKind,
  /** Shown in the run list and written to the audit trail. */
  reason: v.optional(v.string()),
});
export type RunControlRequest = v.InferOutput<typeof RunControlRequest>;

/**
 * Statuses a run can be asked to leave.
 *
 * A finished run is finished — cancelling one is not an error worth a 500, but
 * it is not an action either, and saying so is more useful than pretending.
 */
export const LIVE_RUN_STATUSES = ['pending', 'running', 'awaiting_approval', 'paused'] as const;

export function isLive(status: string): boolean {
  return (LIVE_RUN_STATUSES as readonly string[]).includes(status);
}

/**
 * What a step should do when it reaches a checkpoint.
 *
 * `continue` is the overwhelmingly common answer, which is why this resolves to
 * a single small value rather than the intent row itself — the check runs
 * between every durable step, and it has to be cheap enough that nobody is
 * tempted to skip it.
 */
export const CheckpointVerdict = v.picklist(['continue', 'stop', 'hold']);
export type CheckpointVerdict = v.InferOutput<typeof CheckpointVerdict>;

export function verdictFor(intent: RunIntentKind | null): CheckpointVerdict {
  if (intent === 'cancel') return 'stop';
  if (intent === 'pause') return 'hold';
  return 'continue';
}
